import React, { useMemo, useState } from 'react';
import {
  CheckinMember,
  CheckinState,
  CheckinStep,
  consentFor,
  declareChild,
  getPersonalLink,
  Relationship,
  RELATIONSHIPS,
  recordIdFor,
  scanIdFor,
  startIdentityFor,
} from '../../../../api/stay-checkin-client';
import { personalLink as makePersonalLink, StayPublicError } from '../../../../api/stays-public-client';
import { StatusLabel } from '../../../../motion';
import { FormModal } from '../../../modal/form-modal';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { LinkField, LinkRow } from '../../../onboarding/invite/invite-qr-modal';
import { ErrorText, Field, FieldError, Label, Select } from '../../../onboarding/onboarding.styled';
import {
  Actions,
  CheckRow,
  Ghost,
  PartyActions,
  PartyHead,
  PartyItem,
  PartyList,
  PartyName,
  Primary,
  Prose,
  Quiet,
  Secondary,
  SecondaryLink,
} from '../../../onboarding/stay/public-stay-page.styled';
import { leaveFor, saveReturn } from '../../../onboarding/stay/stay-steps';
import { StayKey, t } from '../../../onboarding/stay/stay-strings';
import { Legend, Note, PersonGrid, StepHeading } from './guest-checkin.styled';
import { InhouseIdPanel } from './inhouse-id-panel';

const L = 'en' as const;

type Patch = { party: CheckinMember[]; steps: CheckinStep[]; completed_at: string | null };

const pillOf = (m: CheckinMember, inhouse: boolean): { tone: StatusTone; key: StayKey } => {
  if (m.identity_mode === 'declared') {
    return m.declared ? { tone: 'neutral', key: 'pillDeclared' } : { tone: 'warning', key: 'pillNeedsDetails' };
  }
  if (m.identity_mode === 'not_required') return { tone: 'neutral', key: 'pillNotNeeded' };
  if (m.identity_mode === 'in_person') return { tone: 'success', key: 'pillInPerson' };
  // IS0 (R-IS0.6): an in-house check never says "verified".
  if (inhouse) {
    if (m.identity === 'verified') {
      return m.id_review === 'needs_review' ? { tone: 'warning', key: 'pillIdHostCheck' } : { tone: 'success', key: 'pillIdConfirmed' };
    }
    if (m.identity === 'failed') return { tone: 'alert', key: 'pillIdRescan' };

    return { tone: 'neutral', key: 'pillNotStarted' };
  }
  if (m.identity === 'verified') return { tone: 'success', key: 'pillVerified' };
  if (m.identity === 'pending') return { tone: 'warning', key: 'pillPending' };
  if (m.identity === 'failed') return { tone: 'alert', key: 'pillFailed' };

  return { tone: 'neutral', key: 'pillNotStarted' };
};

const nameOf = (m: CheckinMember, leadFirst: string) =>
  m.is_lead ? t(L, 'youRow', { first: leadFirst }) : t(L, 'memberRow', { first: m.first_name, initial: m.last_initial });

/* ── one adult's ID check: the privacy notice, then the partner ── */

const VerifyPanel: React.FC<{
  token: string;
  state: CheckinState;
  member: CheckinMember;
  onError: (err: unknown) => string | null;
  onCancel: () => void;
}> = ({ token, state, member, onError, onCancel }) => {
  const [agreed, setAgreed] = useState(member.consent_given);
  const [selfie, setSelfie] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const first = member.is_lead ? state.guest_first_name : member.first_name;

  const go = async () => {
    if (busy || !agreed) return;
    setBusy(true);
    setProblem(null);
    try {
      if (!member.consent_given || selfie) await consentFor(token, member.position, state.consent.version, selfie);
      const out = await startIdentityFor(token, member.position, selfie);
      // BEFORE the hop: /s/return carries no token, so it is kept here.
      saveReturn('s', token);
      leaveFor(out.redirect_url);
    } catch (err) {
      setBusy(false);
      setProblem(onError(err));
    }
  };

  return (
    <PersonGrid>
      <Legend>{t(L, 'consentForTitle', { first })}</Legend>
      <Prose>{state.consent.identity_text}</Prose>
      <Quiet>{t(L, 'privacyVersion', { version: state.consent.version })}</Quiet>
      <CheckRow htmlFor={`agree-${member.position}`}>
        <input
          id={`agree-${member.position}`}
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
        />
        <span>{t(L, 'agree')}</span>
      </CheckRow>
      <CheckRow htmlFor={`selfie-${member.position}`}>
        <input id={`selfie-${member.position}`} type="checkbox" checked={selfie} onChange={e => setSelfie(e.target.checked)} />
        <span>{t(L, 'selfieLabel')}</span>
      </CheckRow>
      {state.consent.selfie_text ? <Quiet>{state.consent.selfie_text}</Quiet> : null}
      {member.attempts_left != null ? <Quiet>{t(L, 'attemptsLeft', { n: member.attempts_left })}</Quiet> : null}
      {problem ? <ErrorText>{problem}</ErrorText> : null}
      <PartyActions>
        <Primary type="button" onClick={go} disabled={!agreed} aria-busy={busy || undefined}>
          <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'continueToVerify'), busy: t(L, 'opening') }} />
        </Primary>
        <Ghost type="button" onClick={onCancel}>
          {t(L, 'cancel')}
        </Ghost>
      </PartyActions>
    </PersonGrid>
  );
};

/* ── an ID check left half-way (tab closed, another device): the server reuses
   the open session with a fresh URL (R-S2.6) and costs no try ── */

const ResumeVerify: React.FC<{
  token: string;
  member: CheckinMember;
  first: string;
  onError: (err: unknown) => string | null;
}> = ({ token, member, first, onError }) => {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const go = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const out = await startIdentityFor(token, member.position, false);
      saveReturn('s', token);
      leaveFor(out.redirect_url);
    } catch (err) {
      setBusy(false);
      setProblem(onError(err));
    }
  };

  return (
    <>
      <Secondary type="button" onClick={go} aria-busy={busy || undefined}>
        <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'resumePerson', { first }), busy: t(L, 'opening') }} />
      </Secondary>
      {problem ? <ErrorText>{problem}</ErrorText> : null}
    </>
  );
};

/* ── a child's details (relationship + who they travel with) ── */

const ChildDetails: React.FC<{
  token: string;
  state: CheckinState;
  member: CheckinMember;
  onSaved: (p: Patch) => void;
  onClose: () => void;
  onError: (err: unknown) => string | null;
}> = ({ token, state, member, onSaved, onClose, onError }) => {
  const adults = state.party.filter(m => m.is_lead || (m.age_band || 'adult') === 'adult');
  const [relationship, setRelationship] = useState<Relationship | ''>(member.relationship || '');
  const [withPos, setWithPos] = useState<string>(member.travels_with != null ? String(member.travels_with) : '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const formId = `child-${member.position}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setFields({});
    setProblem(null);
    try {
      onSaved(
        await declareChild(token, member.position, {
          relationship: relationship || null,
          travels_with: withPos === '' ? null : Number(withPos),
        })
      );
      onClose();
    } catch (err) {
      setBusy(false);
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        setFields(err.fields);

        return;
      }
      setProblem(onError(err));
    }
  };

  return (
    <FormModal
      title={t(L, 'detailsTitle', { first: member.first_name })}
      open
      onClose={onClose}
      saving={busy}
      footer={
        <Primary type="submit" form={formId} aria-busy={busy || undefined}>
          <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'saveDetails'), busy: t(L, 'saving') }} />
        </Primary>
      }
    >
      <form id={formId} onSubmit={submit} noValidate>
        <Field>
          <Label htmlFor={`${formId}-rel`}>{t(L, 'relationshipLabel')}</Label>
          <Select
            id={`${formId}-rel`}
            value={relationship}
            onChange={e => setRelationship(e.target.value as Relationship | '')}
            aria-invalid={fields.relationship ? true : undefined}
          >
            <option value="">{t(L, 'chooseOne')}</option>
            {RELATIONSHIPS.map(r => (
              <option key={r} value={r}>
                {t(L, `rel_${r}` as StayKey)}
              </option>
            ))}
          </Select>
          <FieldError message={fields.relationship} />
        </Field>
        <Field>
          <Label htmlFor={`${formId}-with`}>{t(L, 'travelsWithLabel')}</Label>
          <Select
            id={`${formId}-with`}
            value={withPos}
            onChange={e => setWithPos(e.target.value)}
            aria-invalid={fields.travels_with ? true : undefined}
          >
            <option value="">{t(L, 'chooseOne')}</option>
            {adults.map(a => (
              <option key={a.position} value={String(a.position)}>
                {a.is_lead ? state.guest_first_name : `${a.first_name} ${a.last_initial}`}
              </option>
            ))}
          </Select>
          <FieldError message={fields.travels_with} />
        </Field>
        {problem ? <ErrorText>{problem}</ErrorText> : null}
      </form>
    </FormModal>
  );
};

/* ── one adult's own link (who_verifies = every_adult) ── */

const OwnLink: React.FC<{ token: string; member: CheckinMember; onError: (err: unknown) => string | null }> = ({
  token,
  member,
  onError,
}) => {
  const [link, setLink] = useState<{ url: string; message_text: string; whatsapp_url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const open = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      setLink(member.personal_link ? await getPersonalLink(token, member.position) : await makePersonalLink(token, member.position));
    } catch (err) {
      setProblem(onError(err));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  if (!link) {
    return (
      <>
        <Secondary type="button" onClick={open} aria-busy={busy || undefined}>
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: t(L, 'sendLink', { first: member.first_name }), busy: t(L, 'makingLink') }}
          />
        </Secondary>
        {problem ? <ErrorText>{problem}</ErrorText> : null}
      </>
    );
  }

  return (
    <div data-personal-link="">
      <LinkRow>
        <LinkField
          readOnly
          value={link.url}
          aria-label={t(L, 'linkFor', { first: member.first_name })}
          onFocus={e => e.currentTarget.select()}
        />
        <Secondary type="button" onClick={copy}>
          {copied ? t(L, 'copied') : t(L, 'copyLink')}
        </Secondary>
      </LinkRow>
      <SecondaryLink href={link.whatsapp_url} target="_blank" rel="noopener noreferrer">
        {t(L, 'whatsapp')}
      </SecondaryLink>
    </div>
  );
};

/* ── the step ── */

/**
 * STEP 2 — ID CHECK (plan S6, D-step3; plan ISR moved the naming of the party
 * to step 1, where the lead is guest 1): each person's state. An adult who
 * verifies online gets "Verify {first}" (the
 * privacy notice, then the ID partner — the return comes back to /s/return
 * with no token); a child gets "Add {first}'s details" (relationship and who
 * they travel with — never sent to the ID partner). The states and "Continue"
 * follow the server's steps.
 */
export const IdentityStep: React.FC<{
  token: string;
  state: CheckinState;
  checking: boolean;
  onPatch: (p: Patch) => void;
  onContinue: () => void;
  onEditPeople: () => void;
  onError: (err: unknown) => string | null;
}> = ({ token, state, checking, onPatch, onContinue, onEditPeople, onError }) => {
  const [verifying, setVerifying] = useState<number | null>(null);
  const [childOpen, setChildOpen] = useState<number | null>(null);
  const others = state.party.filter(m => !m.is_lead);
  const need = Math.max(0, (state.trip.adults ?? 1) + (state.trip.minors ?? 0) - 1);
  const formNeeded = others.length !== need || others.some(o => !o.age_band && o.identity_mode !== 'vendor');
  const identityStep = state.steps.find(s => s.id === 'identity');
  const done = identityStep ? identityStep.state === 'done' : false;
  const everyAdult = state.policy.who_verifies === 'every_adult';
  // IS0: the in-house check runs on this page; 'vendor' is the S2 test fake's hosted page.
  const inhouse = state.id_flow !== 'vendor';
  const ordered = useMemo(
    () => [...state.party].sort((a, b) => Number(b.is_lead) - Number(a.is_lead) || a.position - b.position),
    [state.party]
  );
  const waiting = ordered
    .filter(m => !(m.identity_mode === 'not_required' || m.identity_mode === 'in_person'
      || (m.identity_mode === 'declared' ? m.declared : m.identity === 'verified')))
    .map(m => (m.is_lead ? state.guest_first_name : m.first_name));

  // The people are named on step 1 (plan ISR). A party that does not match the
  // trip (an older link saved before step 1 named everyone) is sent back there.
  if (!state.completed_at && formNeeded) {
    return (
      <>
        <StepHeading>{t(L, 'stepIdentity')}</StepHeading>
        <Note role="status">{t(L, 'peopleFirst')}</Note>
        <Actions>
          <Primary type="button" onClick={onEditPeople}>
            {t(L, 'editPeople')}
          </Primary>
        </Actions>
      </>
    );
  }

  return (
    <>
      <StepHeading>{t(L, 'stepIdentity')}</StepHeading>
      <Note>
        {inhouse
          ? everyAdult
            ? t(L, 'identityStepSubInhouse')
            : t(L, 'identityStepSubLeadInhouse')
          : everyAdult
            ? t(L, 'identityStepSub')
            : t(L, 'identityStepSubLead')}
      </Note>
      {checking ? <Quiet role="status">{t(L, 'pending')}</Quiet> : null}
      <PartyList>
        {ordered.map(m => {
          const pill = pillOf(m, inhouse);
          const first = m.is_lead ? state.guest_first_name : m.first_name;
          // In-house: open while not done; after check-in only when the host asked for a new photo ('failed').
          const canAddId = inhouse && m.identity_mode === 'vendor' && m.identity !== 'verified'
            && (!state.completed_at || m.identity === 'failed');
          const canVerify = !inhouse && m.identity_mode === 'vendor' && m.identity !== 'verified' && m.identity !== 'pending'
            && !state.completed_at && (m.attempts_left ?? 1) > 0;
          const canResume = !inhouse && m.identity_mode === 'vendor' && m.identity === 'pending' && !state.completed_at && !checking;
          const needsDetails = m.identity_mode === 'declared' && !state.completed_at;

          return (
            <PartyItem key={m.position} data-position={m.position}>
              <PartyHead>
                <PartyName>{nameOf(m, state.guest_first_name)}</PartyName>
                <StatusPill tone={pill.tone} dot>
                  {t(L, pill.key)}
                </StatusPill>
              </PartyHead>
              {inhouse && m.identity === 'failed' ? <Quiet role="status">{t(L, 'idDoneRescan')}</Quiet> : null}
              {!inhouse && m.identity_mode === 'vendor' && m.identity !== 'verified' && (m.attempts_left ?? 1) <= 0 ? (
                <Quiet role="status">{t(L, 'exhausted')}</Quiet>
              ) : null}
              {verifying === m.position && inhouse ? (
                <InhouseIdPanel
                  idPrefix={`person-${m.position}`}
                  first={first}
                  consent={{ text: state.consent.identity_text, version: state.consent.version, given: m.consent_given }}
                  scanMode={state.id_scan_mode || 'manual_only'}
                  checkInDay={state.stay.check_in}
                  giveConsent={() => consentFor(token, m.position, state.consent.version, false)}
                  scan={photo => scanIdFor(token, m.position, photo)}
                  record={async body => {
                    const out = await recordIdFor(token, m.position, body);
                    setVerifying(null);
                    onPatch({ party: out.state.party, steps: out.state.steps, completed_at: out.state.completed_at });
                  }}
                  onError={onError}
                  onCancel={() => setVerifying(null)}
                />
              ) : verifying === m.position ? (
                <VerifyPanel token={token} state={state} member={m} onError={onError} onCancel={() => setVerifying(null)} />
              ) : (
                <PartyActions>
                  {canAddId ? (
                    <Secondary type="button" onClick={() => setVerifying(m.position)}>
                      {m.identity === 'failed' ? t(L, 'addIdAgain', { first }) : t(L, 'addIdPerson', { first })}
                    </Secondary>
                  ) : null}
                  {canVerify ? (
                    <Secondary type="button" onClick={() => setVerifying(m.position)}>
                      {t(L, 'verifyPerson', { first })}
                    </Secondary>
                  ) : null}
                  {canResume ? <ResumeVerify token={token} member={m} first={first} onError={onError} /> : null}
                  {(canVerify || (canAddId && !state.completed_at)) && everyAdult && !m.is_lead ? (
                    <OwnLink token={token} member={m} onError={onError} />
                  ) : null}
                  {needsDetails ? (
                    <Secondary type="button" onClick={() => setChildOpen(m.position)}>
                      {t(L, 'addDetails', { first })}
                    </Secondary>
                  ) : null}
                </PartyActions>
              )}
            </PartyItem>
          );
        })}
      </PartyList>
      {childOpen != null
        ? (() => {
            const child = state.party.find(p => p.position === childOpen);

            return child ? (
              <ChildDetails
                token={token}
                state={state}
                member={child}
                onSaved={onPatch}
                onClose={() => setChildOpen(null)}
                onError={onError}
              />
            ) : null;
          })()
        : null}
      <Actions>
        {!done && waiting.length ? (
          <Quiet id="identity-waiting">{t(L, 'identityWaiting', { names: waiting.join(', ') })}</Quiet>
        ) : null}
        <Primary
          type="button"
          onClick={onContinue}
          disabled={!done}
          aria-describedby={!done && waiting.length ? 'identity-waiting' : undefined}
        >
          {t(L, 'continue')}
        </Primary>
        {!state.completed_at && others.some(o => !o.locked) ? (
          <Ghost type="button" onClick={onEditPeople}>
            {t(L, 'editPeople')}
          </Ghost>
        ) : null}
      </Actions>
    </>
  );
};
